import "amazon-connect-streams";
import { immerable } from "immer";

import type { ChannelKind, Locale } from "@/sdk";
import type { CaseDetails } from "@/shared-types";

import { Task } from "@/sdk/agent/task";
import { useSessionStore } from "@/sdk/sessions/session.store";

import { getPreviousTaskFromSession } from "./session-tasks";

/**
 * Creates a standardized config from an Amazon Connect contact
 * to ensure consistent initialization across subclasses.
 *
 * @param contact - The Amazon Connect contact object
 * @throws {TypeError} If contact type is not VOICE or CHAT
 */
export function getAmazonContactConfig(contact: connect.Contact): AmazonConnectContactConfig {
    const type = contact.getType();
    if (type !== connect.ContactType.VOICE && type !== connect.ContactType.CHAT) {
        throw new TypeError(`contact type not supported: ${type}`);
    }
    const id = contact.getContactId();
    const createdOn = contact.getQueueTimestamp();
    const sessions = useSessionStore.getState();
    /**
     * WARNING: Using activeSession logic here will cause issues for Multi-session concurrency support
     */
    const activeSession = sessions.sessions.find((session) => session.id === sessions.selectedSessionId);

    /**
     * TODO: Refactor channel-specific logic: logic differs by channel but lives in shared factory
     * Voice contact logic depends on active session which can't be used for multi-session concurrency channels
     */
    const sessionId =
        type === connect.ContactType.VOICE ? (activeSession?.id ?? contact.getContactId()) : contact.getContactId();

    const previousTaskId =
        type === connect.ContactType.VOICE && activeSession
            ? getPreviousTaskFromSession(activeSession, id)?.id
            : undefined;

    return { type, id, createdOn, contact, sessionId, previousTaskId };
}

export interface AmazonConnectContactConfig {
    type: ChannelKind;
    id: string;
    createdOn: Date;
    contact: connect.Contact;
    sessionId: string;
    /** Only set for voice contacts (callbacks/transfers). Always undefined for chat. */
    previousTaskId: string | undefined;
}

export default abstract class AmazonConnectContact extends Task {
    override [immerable] = true;

    sessionId: string;
    /** Only set for voice contacts (callbacks/transfers). Always undefined for chat. */
    previousTaskId: string | undefined;
    // todo: find a way to set when the contact/task was assigned to the expert (not when the contact was connected)
    public assignedAt: Date | undefined;

    public readonly config: AmazonConnectContactConfig;
    public readonly locale: Locale | undefined;

    constructor(name: string, config: AmazonConnectContactConfig, locale: Locale | undefined) {
        super(config.type, config.id, name, config.createdOn);
        this.config = config;
        this.locale = locale;
        this.sessionId = config.sessionId;
        this.previousTaskId = config.previousTaskId;
        this.statusReason = undefined; // TODO
    }

    /** Not applicable for Amazon Connect; always returns the same as {@link sessionId} */
    get sessionIdFallback(): string {
        return this.sessionId;
    }

    public static getContactName(config: AmazonConnectContactConfig): string {
        return `${config.type}-${config.id}`;
    }

    public override get caseDetails(): CaseDetails | undefined {
        // TODO
        return undefined;
    }

    public override async updateCaseDetails(_caseDetailsUpdates: Partial<CaseDetails>): Promise<void> {
        // TODO
    }

    public override autoAccept = false;

    public get analyticsData(): Record<string, string> {
        return {
            ContactId: this.config.contact.getContactId(),
        };
    }
}
